/**
 * Admin — Creator Incentive tab.
 * Shows all creators + monthly totals + WhatsApp reminder link builder.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Trophy,
  Video,
  Zap,
  IndianRupee,
  MessageCircle,
  Copy,
  Calendar,
  Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/creator-incentive`;

export default function AdminCreatorIncentiveTab() {
  const [creators, setCreators] = useState([]);
  const [totals, setTotals] = useState({ creators: 0, total_earnings: 0, total_shorts: 0, total_videos: 0 });
  const [monthly, setMonthly] = useState([]);
  const [monthTotals, setMonthTotals] = useState({ shorts: 0, videos: 0, amount: 0, count: 0 });
  const [monthFilter, setMonthFilter] = useState("");
  const [q, setQ] = useState("");

  const loadCreators = async () => {
    try {
      const res = await axios.get(`${API}/admin/creators`, { withCredentials: true });
      setCreators(res.data.creators || []);
      setTotals(res.data.totals || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load creators");
    }
  };

  const loadMonthly = async () => {
    try {
      const params = monthFilter ? { month: monthFilter } : {};
      const res = await axios.get(`${API}/admin/monthly-report`, { params, withCredentials: true });
      setMonthly(res.data.monthly || []);
      setMonthTotals(res.data.totals || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load monthly report");
    }
  };

  useEffect(() => { loadCreators(); loadMonthly(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMonthly(); /* eslint-disable-next-line */ }, [monthFilter]);

  const sendReminder = async (creator) => {
    if (!creator.whatsapp) return toast.error(`${creator.user_name} has not saved a WhatsApp number yet`);
    try {
      const res = await axios.get(
        `${API}/admin/reminder-link/${creator.user_id}`,
        { withCredentials: true },
      );
      // Open WhatsApp Web with pre-filled message
      window.open(res.data.link, "_blank", "noopener,noreferrer");
      toast.success("WhatsApp opened — send when ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reminder link failed");
    }
  };

  const copyReminderLink = async (creator) => {
    try {
      const res = await axios.get(
        `${API}/admin/reminder-link/${creator.user_id}`,
        { withCredentials: true },
      );
      await navigator.clipboard.writeText(res.data.link);
      toast.success("WhatsApp link copied");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copy failed");
    }
  };

  const filtered = creators.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (c.user_name || "").toLowerCase().includes(s) ||
           (c.user_email || "").toLowerCase().includes(s) ||
           (c.whatsapp || "").includes(s);
  });

  return (
    <div className="space-y-6" data-testid="admin-creator-incentive-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest"><Users className="w-3.5 h-3.5" /> Creators</div>
          <p className="text-3xl font-semibold mt-2">{totals.creators || 0}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest"><Trophy className="w-3.5 h-3.5" /> Total payout</div>
          <p className="text-3xl font-semibold mt-2 flex items-center"><IndianRupee className="w-6 h-6" />{(totals.total_earnings || 0).toLocaleString("en-IN")}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest"><Video className="w-3.5 h-3.5" /> Videos</div>
          <p className="text-3xl font-semibold mt-2">{totals.total_videos || 0}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest"><Zap className="w-3.5 h-3.5" /> Shorts</div>
          <p className="text-3xl font-semibold mt-2">{totals.total_shorts || 0}</p>
        </Card>
      </div>

      {/* Monthly report */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-neutral-600" />
            <h3 className="text-lg font-semibold">Monthly report</h3>
            {monthFilter && <Badge variant="secondary">{monthFilter}</Badge>}
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="YYYY-MM (leave empty for all months)"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-56 h-9"
              data-testid="admin-incentive-month-filter"
            />
            <div className="text-sm text-neutral-500 flex items-center whitespace-nowrap">
              <IndianRupee className="w-3 h-3" />{(monthTotals.amount || 0).toLocaleString("en-IN")} · {monthTotals.videos || 0}v · {monthTotals.shorts || 0}s
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="py-2">Month</th><th>Videos</th><th>Shorts</th><th className="text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {monthly.length === 0 && (
                <tr><td colSpan="4" className="py-6 text-center text-neutral-400">No data yet</td></tr>
              )}
              {monthly.map((m) => (
                <tr key={m.month} className="border-b border-neutral-100">
                  <td className="py-2 font-medium">{m.month}</td>
                  <td>{m.videos}</td>
                  <td>{m.shorts}</td>
                  <td className="text-right font-semibold">₹{m.amount.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Creators list */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">All creators</h3>
          <div className="relative w-72">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search creator / email / whatsapp"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9"
              data-testid="admin-incentive-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-incentive-creators-table">
            <thead className="text-left text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="py-2">Creator</th>
                <th>WhatsApp</th>
                <th>Videos</th>
                <th>Shorts</th>
                <th>Total</th>
                <th>Last</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="py-6 text-center text-neutral-400">No creators match</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.user_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-3">
                    <p className="font-medium text-[#111]">{c.user_name || "—"}</p>
                    <p className="text-[11px] text-neutral-500">{c.user_email}</p>
                  </td>
                  <td className="text-neutral-600">{c.whatsapp || <span className="text-neutral-400">—</span>}</td>
                  <td>{c.videos}</td>
                  <td>{c.shorts}</td>
                  <td className="font-semibold">₹{c.total_earnings.toLocaleString("en-IN")}</td>
                  <td className="text-[11px] text-neutral-500">
                    {c.last_submission ? new Date(c.last_submission).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendReminder(c)}
                        disabled={!c.whatsapp}
                        className="h-8 text-xs"
                        data-testid={`admin-incentive-remind-${c.user_id}`}
                        title={c.whatsapp ? "Open WhatsApp with reminder" : "No WhatsApp saved"}
                      >
                        <MessageCircle className="w-3 h-3 mr-1" /> Remind
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyReminderLink(c)}
                        disabled={!c.whatsapp}
                        className="h-8 text-xs"
                        title="Copy WhatsApp link"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
